import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import {
  ActiveConfig,
  ImageType,
  LogodeletePayload,
  LogosResultTypeList,
  LogosTypeList,
  PopupSettingType,
  PopupSettings,
  WidgetNames,
  WorkflowConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, filter, takeUntil, tap } from 'rxjs';
import { ActiveConfigService } from '../../../libs/services/active-config/active-config.service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  CardBackgroundImageSelected,
  CardFlipIconSelected,
} from '../../../libs/actions/card-background.action';
import { PageBackgroundImageSelected } from '../../../libs/actions/page-background-workflow.action';
import { ProgressBarImageSelected } from '../../../libs/actions/progress-bar-widget-workflow.action';
import { ApiService } from '../../../libs/services/api-service/api.service';
import { AppBuilderChangesStatusService } from '../../../libs/services/app-builder-changes-service/app-builder-changes-status.service';
import { WorkflowConfigService } from '../../../libs/services/workflow-config/workflow-config.service';
import { WorkflowCommonService } from '../../../libs/services/workflow-config/workflow.common.service';
import { AppGridState } from '../../../libs/state/app-grid.state';
import { ProjectsState } from '../../../libs/state/project.state';
import { AutoDestroy } from '../../../libs/utils/utils';

@Component({
  standalone: false,
  selector: 'finxone-workflow-frontend-image-upload-popup',
  templateUrl: './image-upload-popup.component.html',
  styleUrls: ['./image-upload-popup.component.scss'],
})
export class ImageUploadPopupComponent implements OnInit, OnChanges {
  @AutoDestroy destroy$ = new Subject<void>();
  @Input() public isFromGlobalStyling = false;
  @Input() popupSetting: PopupSettings = {
    show: false,
    type: PopupSettingType.IMAGE_WIDGET,
  };
  imageType = ImageType;
  public files: any[] = [];
  uniqueId: number | string;
  errorMessages: string[] = [];
  uploadProgress = 0;
  showIndividualMenu: { [key: number]: boolean } = {};
  processingImages = true;
  workflowConfig!: WorkflowConfig;
  activeConfig: ActiveConfig;
  apiResponse: any[];
  isShowLogoImages = false;
  defaultImages: LogosResultTypeList[];
  selectedlogo: LogosResultTypeList = {
    id: '',
    filename: '',
    path: '',
    global_status: false,
  };
  isCurrentLogoid: string;
  isShowMoreOption = false;
  pageNumber = 1;
  protected readonly PopupSettingType = PopupSettingType;

  @Select(AppGridState.getNewWidgetDetails)
  newWidgetDetails$!: Observable<{
    widgetName: string;
    uniqueId: string | number;
  }>;

  public imageAcceptanceType = 'image/x-png,image/jpeg,image/svg+xml,image/webp';
  widgetDetails: { widgetName: string; uniqueId: string | number } | undefined;

  constructor(
    private apiService: ApiService,
    private workflowConfigService: WorkflowConfigService,
    private workflowCommonService: WorkflowCommonService,
    private activeConfigService: ActiveConfigService,
    private appBuilderChangesStatusService: AppBuilderChangesStatusService,
    private store: Store,
    private alertHandlerService: AlertHandlerService,
  ) {
    this.workflowConfigService
      .getConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((config: WorkflowConfig) => {
        this.workflowConfig = config;
      });

    this.newWidgetDetails$
      .pipe(
        takeUntil(this.destroy$),
        filter((widgetDetails) => {
          return [WidgetNames.IMAGE_WIDGET.toString()].includes(widgetDetails.widgetName);
        }),
      )
      .subscribe((widgetDetails) => {
        this.widgetDetails = widgetDetails;
        this.uniqueId = widgetDetails.uniqueId;
      });
  }

  ngOnInit(): void {
    this.activeConfigService
      .getConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((config: ActiveConfig) => {
        if (this.popupSetting.type === PopupSettingType.IMAGE_LIBRARY) {
          this.isShowLogoImages = true;
        } else {
          this.isShowLogoImages = false;
        }
        this.activeConfig = config;
      });

    this.apiResponse = [];
  }

  ngOnChanges() {
    this.pageNumber = 1;
    if (this.popupSetting.type == PopupSettingType.IMAGE_LIBRARY) {
      this.isCurrentLogoid = '';
      this.isShowMoreOption = false;
      this.isShowLogoImages = true;
    } else {
      this.isShowLogoImages = false;
    }
    const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
    this.onGetImages(projectId);
  }

  get categoryOfImageAPI(): ImageType {
    if (this.widgetDetails == null) {
      if (this.popupSetting.type === PopupSettingType.LOGO_WIDGET) {
        return ImageType.LOGO;
      } else return ImageType.IMAGE;
    }

    if (this.popupSetting.type === PopupSettingType.IMAGE_LIBRARY) {
      return ImageType.ALL; //to load all types of images for image library
    }

    switch (this.widgetDetails.widgetName) {
      case WidgetNames.IMAGE_WIDGET:
        return ImageType.IMAGE;
      default:
        return ImageType.IMAGE;
    }
  }

  onGetImages(projectid: string) {
    this.apiService
      .onGetImages(projectid, this.pageNumber, this.categoryOfImageAPI)
      .subscribe((res: LogosTypeList) => {
        this.onSetImages(res);
      });
  }

  onSetImages(res: LogosTypeList) {
    let resultArray = res.result;
    if (this.widgetDetails?.widgetName === WidgetNames.IMAGE_WIDGET) {
      resultArray = res.result.map((item) => {
        return item;
      });
    }
    if (this.pageNumber > 1) {
      for (const logo of resultArray) {
        this.defaultImages.push(logo);
      }
    } else {
      this.defaultImages = resultArray;
    }
  }

  isSVGFile(fileName: string) {
    return /.svg/.exec(fileName);
  }

  /**
   * on file drop handler
   */
  onFileDropped(event: any) {
    this.prepareFilesList(event);
  }

  /**
   * handle file from browsing
   */
  fileBrowseHandler(event: any) {
    this.prepareFilesList(event.target.files);
  }

  /**
   * Remove error message if the file is removed
   * @param fileName (File name)
   */
  removeErrorMsg(fileName: string, indexNumber: number) {
    this.errorMessages.forEach((msg: string, index: number) => {
      if (msg.includes(fileName) || indexNumber === index) this.errorMessages.splice(index, 1);
    });
  }

  /**
   * Convert Files list to normal array list
   * @param files (Files List)
   */
  prepareFilesList(files: Array<any>) {
    for (const item of files) {
      item.hasSizeError = false;
      item.progress = 0;
      if (
        this.popupSetting.type === PopupSettingType.IMAGE_WIDGET ||
        this.popupSetting.type === PopupSettingType.IMAGE_LIBRARY
      ) {
        this.files = [];
      }
      this.files.push(item);
      if (this.files.length > 0) this.showIndividualMenu[this.files.indexOf(item)] = false;
      if (item.size > 2097152) {
        const msg = 'Cant upload ' + item.name + ', it exceeds the limit of 2 MB.';
        this.errorMessages.push(msg);
        item.hasSizeError = true;
      }
    }
    const filesToUpload = this.files.filter((file: any) => {
      return file.hasSizeError === false;
    });
    this.processingImages = false;
    this.uploadFiles(filesToUpload);
  }

  /**
   *
   * @param files  (Image Files List)
   */
  uploadFiles(files: File[]) {
    const formData: FormData = new FormData();
    files.forEach((file: File) => {
      formData.append('myfiles', file);
    });
    const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
    formData.append('projectId', projectId);
    if (this.isShowLogoImages) {
      formData.append('category', 'logo');
    } else {
      formData.append('category', 'image');
    }
    this.apiService
      .uploadFormData('/file/upload-file', formData)
      .pipe(
        tap((event: any) => {
          this.appBuilderChangesStatusService.appBuilderStatus = 'true';
          if (event.type === HttpEventType.UploadProgress) {
            this.uploadProgress = Math.round((100 * event.loaded) / event.total);
          }
          if (event.loaded === 100) {
            this.processingImages = true;
          }
        }),
        tap((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            this.alertHandlerService.showAlertFn('success', 'You successfully updated the file.');
            this.processingImages = false;
            this.apiResponse = event.body;
            this.pageNumber = 1;
            const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
            this.onGetImages(projectId);
            this.popupSettingAfterUpload(event, files);
          }
        }),
        tap({
          error: (error: any) => {
            let msg;
            if (typeof error.error.message === 'string') {
              msg = error.error.message;
            } else {
              msg = error.error.message.join(', ');
            }

            this.alertHandlerService.showAlertFn('error', msg);
            this.processingImages = false;
            this.files.forEach((file: any) => {
              file.hasSizeError = true;
            });
          },
        }),
      )
      .subscribe();
  }

  popupSettingAfterUpload(event: HttpResponse<any>, files: File[]) {
    if (this.popupSetting.type === PopupSettingType.PAGE_BACKGROUND) {
      this.store.dispatch(new PageBackgroundImageSelected(event.body[0].path));
    } else if (this.popupSetting.type === WidgetNames.CARD) {
      if (this.popupSetting.data) {
        this.store.dispatch(new CardBackgroundImageSelected(event.body[0].path, this.popupSetting.data));
      }
    } else if (this.popupSetting.type != PopupSettingType.IMAGE_LIBRARY) {
      this.updateWorkflowConfigData(event.body, files);
    }
  }

  /**
   * Display menu for file user clicked 3 dots on
   */
  toggleMenu(index: number) {
    this.showIndividualMenu[index] = !this.showIndividualMenu[index];
  }

  updateWorkflowConfigData(response: any, files?: File[]): void {
    this.workflowCommonService.updateImageWidgetConfig(
      this.uniqueId,
      response,
      (this.popupSetting?.type as WidgetNames) ?? WidgetNames.IMAGE_WIDGET,
      files,
    );
  }

  closePopup(type: string) {
    if (type == 'cancel') {
      this.popupSetting.show = false;
      this.files = [];
      this.errorMessages = [];
      if (
        this.popupSetting.type !== PopupSettingType.PAGE_BACKGROUND &&
        this.popupSetting.type !== PopupSettingType.IMAGE_LIBRARY &&
        this.popupSetting.type !== WidgetNames.CARD
      ) {
        this.removeWidgetFromConfig();
      }
      this.apiResponse = [];
      this.processingImages = false;
    } else {
      const selectedlogos = this.defaultImages.filter((x) => x.isActive === true);
      if (selectedlogos.length) {
        this.updateWorkflowConfigData(selectedlogos);
      }

      this.popupSetting.show = false;
      this.files = [];
      this.errorMessages = [];
      this.apiResponse = [];
      this.processingImages = false;
    }
  }

  removeWidgetFromConfig() {
    this.workflowCommonService.removeLastAddedWidget();
  }

  /**
   *
   * @param imagesArrayIndex - Index of image in carosel images array
   */
  removeImage(imagesArrayIndex: number) {
    const imageId = this.apiResponse[imagesArrayIndex] ? this.apiResponse[imagesArrayIndex].id : undefined;
    const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
    const inputObject = {
      id: [imageId],
      projectId: projectId,
    };
    this.apiService
      .deleteCall('/file', inputObject)
      .pipe(
        tap((event: any) => {
          if (event === true) {
            this.appBuilderChangesStatusService.appBuilderStatus = 'true';
            this.alertHandlerService.showAlertFn('success', 'You successfully removed the file.');
            this.files.splice(imagesArrayIndex, 1);
            this.apiResponse.splice(imagesArrayIndex, 1);
            this.showIndividualMenu[imagesArrayIndex] = false;
            this.updateWorkflowConfigData(this.apiResponse, this.files);
          }
        }),
      )
      .subscribe();
  }

  selectLogo(logo: LogosResultTypeList) {
    this.selectedlogo = logo;

    if (this.popupSetting.type === 'page_background') {
      this.store.dispatch(new PageBackgroundImageSelected(logo.path));
    } else if (this.popupSetting.type === WidgetNames.CARD) {
      if (this.popupSetting.data) {
        this.store.dispatch(new CardBackgroundImageSelected(logo.path, this.popupSetting.data));
      } else {
        if (!this.isSVGFile(this.selectedlogo.filename)) {
          this.alertHandlerService.showAlertFn('error', 'Please select a valid SVG file');
          return;
        } else {
          this.store.dispatch(new CardFlipIconSelected(logo.path));
        }
      }
    } else {
      this.updateWorkflowConfigData([logo]);
    }
    this.closePopup('done');
  }

  deleteLogo(id: string) {
    const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
    const payload: LogodeletePayload = {
      id: [id],
      projectId: projectId,
    };
    this.apiService.deletelogo(payload).subscribe((res) => {
      this.appBuilderChangesStatusService.appBuilderStatus = 'true';
      this.pageNumber = 1;
      const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
      this.onGetImages(projectId);
      this.alertHandlerService.showAlertFn('success', 'Delete Logo Successfully');
    });
  }

  showMore(id: string) {
    if (this.isCurrentLogoid == id) {
      this.isShowMoreOption = !this.isShowMoreOption;
    } else {
      this.isShowMoreOption = true;
    }

    this.isCurrentLogoid = id;
  }

  setGlobalLogo(id: string) {
    const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
    this.apiService.setGloballogo(id, projectId).subscribe(() => {
      this.isCurrentLogoid = '';
      this.isShowMoreOption = false;
      this.pageNumber = 1;
      this.onGetImages(projectId);
      this.alertHandlerService.showAlertFn('success', 'Logo set as global Successfully');
    });
  }

  onScroll(event: { target: any }) {
    if (Math.ceil(event.target.offsetHeight + event.target.scrollTop) >= event.target.scrollHeight) {
      this.pageNumber = this.pageNumber + 1;
      const projectId = this.store.selectSnapshot(ProjectsState.getActiveProject).id;
      this.onGetImages(projectId);
    }
  }
}
